import { proxy as comlinkProxy } from 'comlink';
import { Dictionary, isEmpty, keyBy, unionBy } from 'lodash';
import _flatMap from 'lodash/flatMap';
import _keyBy from 'lodash/keyBy';
import _unionBy from 'lodash/unionBy';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { ProjectId, Team, User, UserStatus, UserType } from 'api/models';
import { ProjectAPI } from 'api/project';
import { BulkOrgChartUpdate, UserAPI } from 'api/user';
import { toastService } from 'components/molecules/toast';
import {
  Member,
  MembersAPI,
  MembersResponse,
  MembersV2Response,
  UpdateExternalMembers,
} from 'data/members/api';
import { useTranslate } from 'hooks/use-translation';
import {
  getProjectMembersQueryKey,
  queryClient as queryClientDefault,
} from 'queries';
import { Company } from 'stores/companies';
import { putAll as putAllTeams } from 'stores/teams';
import { putAll as putAllUsers } from 'stores/users';
import { ActiveUsersAndTeams } from 'utils/active-users-and-teams';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { oneDay, oneHour } from './constants';
import {
  getPeopleHierarchyQueryKey,
  getPeopleListQueryKey,
} from './customer-company';
import { useHideNonStrategicPartner } from './partner';

export const MEMBERS_V2_QUERY_KEY = ['members-v2'];
export const MEMBERS_V2_MODIFIED_QUERY_KEY = ['members-v2-modified'];

export const ALL_MEMBERS_QUERY_KEY = ['all-members'];
export const ALL_MEMBERS_MAP_QUERY_KEY = ['all-members-map'];
type BaseMembersQueryOptions<TData = unknown> = UseQueryOptions<
  MembersResponse,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type BaseMembersV2QueryOptions<TData = unknown> = UseQueryOptions<
  MembersV2Response,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type MembersQueryOptions = BaseMembersQueryOptions<MembersResponse>;
type MembersV2QueryOptions = BaseMembersV2QueryOptions<MembersV2Response>;

export type MembersQueryResult = UseQueryResult<MembersResponse, unknown>;
type MembersV2QueryResult = UseQueryResult<MembersV2Response, unknown>;

export const BaseMembersQueryOptions: Pick<
  BaseMembersQueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: ALL_MEMBERS_QUERY_KEY,
  queryFn: () => MembersAPI.findAll(),
  staleTime: oneDay,
};

const BaseMembersV2QueryOptions: Pick<
  BaseMembersV2QueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: MEMBERS_V2_QUERY_KEY,
  staleTime: oneHour,
};

export const activeUsersAndTeamsFromIndexedB = new ActiveUsersAndTeams();

// @TODO: This need to be moved as a custom hook and called inside a component.
// @TODO: Remove the react query call and move the new memebers api call inside the worker.
function processMembersDataInWorker(
  documents: {
    users: User[];
    teams: Team[];
  },
  onDataSeededIntoIndexedDB?: () => void,
) {
  const worker = callWorkerWithCleanup();

  worker.workerApi.processMembersData(
    documents,
    comlinkProxy(({ users, teams }) => {
      activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
        users,
        teams,
      };
      onDataSeededIntoIndexedDB?.();
      // clean up worker after members and teams data inserted
      worker.cleanup();
    }),
  );
}

const transformMembers = (
  data: MembersV2Response,
  isCurrentUserCustomer: () => boolean,
  updateCompanyIfNonStrategic: (company: Company) => Company,
) => {
  const suggestionsData = [...Object.values(data.users)];

  const membersData: Array<User> = suggestionsData.map((user: User) => ({
    ...user,
    userId: user.userId,
  }));

  const users = isCurrentUserCustomer()
    ? membersData.map((member) => ({
        ...member,
        company: updateCompanyIfNonStrategic(member.company),
      }))
    : membersData;
  const teams = Object.values(data.teams);

  return { users, teams };
};

export const useMembersV2Query = (
  {
    queryKey,
    queryFn,
    staleTime,
    select,
    ...restOptions
  }: MembersV2QueryOptions,
  onDataSeededIntoIndexedDB?: () => void,
): MembersV2QueryResult => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isCustomerPreview = !!queryParams.get('customer-preview');
  const {
    isDependentEntitiesLoading,
    isCurrentUserCustomer,
    updateCompanyIfNonStrategic,
  } = useHideNonStrategicPartner();

  return useQuery({
    ...BaseMembersV2QueryOptions,
    queryFn: () =>
      MembersAPI.getMembersV2().then((data) => {
        // These dispatch methods were moved from app-boot-data.tsx after removing the api call to /members on page load
        const { users, teams } = transformMembers(
          data,
          isCurrentUserCustomer,
          updateCompanyIfNonStrategic,
        );

        dispatch(putAllUsers([]));
        dispatch(putAllTeams(teams ?? []));

        // In project customer preview we don't want to re-populate the indexDb with just members of a specific project.
        if (isCustomerPreview) {
          onDataSeededIntoIndexedDB?.();
        } else {
          // Handle storing data to indexeddb inside the web worker
          processMembersDataInWorker(
            { teams, users },
            onDataSeededIntoIndexedDB,
          );
        }

        return data;
      }),
    enabled: !isDependentEntitiesLoading,
    ...restOptions,
    refetchOnWindowFocus: false,
  });
};

export const useMembersV2TransformedQuery = (): UseQueryResult<{
  users: User[];
  teams: Team[];
}> => {
  const { isCurrentUserCustomer, updateCompanyIfNonStrategic } =
    useHideNonStrategicPartner();

  return useQuery(
    MEMBERS_V2_MODIFIED_QUERY_KEY,
    async () => {
      let data = queryClientDefault.getQueryData(MEMBERS_V2_QUERY_KEY) as
        | MembersV2Response
        | undefined;

      if (!data) {
        data = await MembersAPI.getMembersV2();

        if (!data) {
          return {};
        }
      }

      const { users, teams } = transformMembers(
        data,
        isCurrentUserCustomer,
        updateCompanyIfNonStrategic,
      );

      return {
        users,
        teams,
      };
    },
    {},
  );
};

export const useMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  return useQuery({
    ...BaseMembersQueryOptions,
    ...restOptions,
  });
};

export const useNativeMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  select,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  return useQuery({
    ...BaseMembersQueryOptions,
    select: ({ users, members, ...rest }) => {
      const result: MembersResponse = {
        ...rest,
        users: (users ?? []).filter(
          ({ userType }) => userType === UserType.Native,
        ),
        members: (members ?? []).filter(
          ({ user }) => user.userType === UserType.Native,
        ),
      };

      return result;
    },
    ...restOptions,
  });
};

export const ALL_USERS_QUERY_KEY = ALL_MEMBERS_QUERY_KEY;
export const useUsersQuery = (): UseQueryResult<User[]> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members.map((member) => member.user);
      },
    },
  );
};

export const useUsersMapQuery = (): UseQueryResult<Map<number, User>> => {
  return useQuery<
    MembersResponse,
    unknown,
    Map<number, User>,
    typeof ALL_MEMBERS_MAP_QUERY_KEY
  >({
    ...BaseMembersQueryOptions,
    select: (res) => {
      return new Map<number, User>(
        res.members.map((member) => [member.user.userId, member.user]),
      );
    },
  });
};

export const useNativeActiveOrInvitedUsersQuery = (): UseQueryResult<
  User[]
> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(
            ({ userStatus, userType }) =>
              (userStatus === UserStatus.Active ||
                userStatus === UserStatus.Invited) &&
              userType === UserType.Native,
          );
      },
      refetchOnMount: true,
    },
  );
};

export const useMembersFieldsAccessMetaQuery = (): UseQueryResult<
  MembersResponse['fieldsAccessMeta']
> => {
  return useQuery<
    MembersResponse,
    unknown,
    MembersResponse['fieldsAccessMeta'],
    typeof ALL_USERS_QUERY_KEY
  >({
    ...BaseMembersQueryOptions,
    select: (res) => res.fieldsAccessMeta,
  });
};

export const useNativeUsersQuery = (): UseQueryResult<User[]> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType }) => userType !== UserType.External);
      },
    },
  );
};

export const useActiveOrInvitedNonCustomerUsersQuery = (): UseQueryResult<
  User[]
> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType, userStatus }) => {
            return (
              userType !== UserType.External &&
              userStatus !== UserStatus.Inactive
            );
          });
      },
    },
  );
};

export const useCustomerUsersQuery = (): UseQueryResult<User[]> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType }) => {
            return userType === UserType.External;
          });
      },
    },
  );
};

const patchUsersByPayload = (previous: MembersResponse, payload: User) => {
  if (!payload) {
    return previous;
  }
  const index = previous.members.findIndex(
    (x) => x.user.userId === payload.userId,
  );

  if (index === -1) {
    return previous;
  }
  const newMembers: MembersResponse = { ...previous };

  newMembers.members[index].user = {
    ...previous.members[index].user,
    ...payload,
    fields: unionBy(
      payload.fields ?? [],
      previous.members[index].user.fields ?? [],
      'fieldId',
    ),
  };

  if (previous.users !== undefined && newMembers.users !== undefined) {
    const userIndex = previous.users.findIndex(
      (x) => x.userId === payload.userId,
    );

    newMembers.users[userIndex] = {
      ...previous.users[userIndex],
      ...payload,
      fields: unionBy(
        payload.fields ?? [],
        previous.members[userIndex].user.fields ?? [],
        'fieldId',
      ),
    };
  }

  return newMembers;
};

export const useUpdateUserQuery = () => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation((payload: User) => UserAPI.save(payload), {
    onMutate: async (payload) => {
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      if (previous) {
        const newMembers = patchUsersByPayload(previous, payload);

        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
      }

      return {
        fallback: previous,
      };
    },
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) => {
      const { fallback } = context || {};

      if (fallback) {
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
      }
      if (err instanceof Error && err?.request?.status === 403) {
        const errorMsg = translate('settings.team_members.edit.error');

        toastService({
          message: errorMsg,
        }).error();
      }
    },
    onSuccess: (data) => {
      // queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      if (previous) {
        const newMembers = patchUsersByPayload(previous, data);

        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
      }
      queryClient.invalidateQueries(
        getPeopleHierarchyQueryKey(data.company.companyId),
      );
      queryClient.invalidateQueries(
        getPeopleListQueryKey(data.company.companyId),
      );
    },
  });
};

const USER_LOOKUP_KEY = 'USER_TIMEOFF_LOOKUP';

export const useUserTimeoffLookup = (): UseQueryResult<Dictionary<User>> => {
  const { data: nativeActiveUsers = [] } = useNativeActiveOrInvitedUsersQuery();

  return useQuery(USER_LOOKUP_KEY, () => {
    return keyBy(nativeActiveUsers, 'userId');
  });
};

export type PartialMember = Pick<Member, 'user' | 'permissionSet'>;

const patchMembersByPayload = (
  previous: MembersResponse,
  payload: PartialMember,
) => {
  if (!payload) {
    return previous;
  }
  const index = previous.members.findIndex(
    (x) => x.user.userId === payload.user.userId,
  );

  if (index === -1) {
    return previous;
  }

  const newMembers: MembersResponse = JSON.parse(JSON.stringify(previous));
  const existingMember = newMembers.members[index];

  const updatedMember = {
    ...existingMember,
    ...payload,
    user: {
      ...existingMember.user,
      ...payload.user,
      fields: unionBy(
        payload.user.fields ?? [],
        existingMember.user.fields ?? [],
        'fieldId',
      ),
    },
  };

  newMembers.members[index] = updatedMember;

  return newMembers;
};

export const useTeamMemberGridToUpdate = () => {
  const qc = useQueryClient();
  const translate = useTranslate();

  return useMutation(
    (payload: PartialMember) => MembersAPI.updateMember(payload),
    {
      onMutate: async (payload) => {
        const previous: MembersResponse | undefined = qc.getQueryData(
          ALL_MEMBERS_QUERY_KEY,
        );

        if (previous) {
          const newMembers = patchMembersByPayload(previous, payload);

          qc.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
        }

        return {
          fallback: previous,
        };
      },
      onError: (
        err: Record<string, Record<string, unknown>>,
        _payload,
        context,
      ) => {
        const { fallback } = context || {};

        if (fallback) {
          qc.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
        }
        if (err instanceof Error) {
          const errorMsg = translate('settings.team_members.edit.error');

          toastService({
            message: errorMsg,
          }).error();
        }
      },
      onSuccess: () => {
        qc.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      },
    },
  );
};

export const getProjectMembersQueryKeyV2 = ([, projectId]: ProjectId): [
  string,
  number,
] => ['squad', projectId];

export const useProjectMembersInSettingsPageQuery = (
  projectId: ProjectId,
  options = {},
) =>
  useQuery(
    getProjectMembersQueryKey(projectId),
    () => ProjectAPI.getMembers(projectId[1]),
    {
      ...options,
    },
  );

export const useUpdateExternalMembersMutation = (): UseMutationResult<
  any, // response type
  unknown,
  UpdateExternalMembers
> => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) => MembersAPI.updateExternalMembers(payload),
    onSuccess: (_, variables) => {
      const ProjectID = (id: number): ProjectId => ['project_id', id];

      qc.invalidateQueries(
        getProjectMembersQueryKeyV2(ProjectID(variables.projectId)),
      );
    },
  });
};

export const useBulkUpdateOrgChart = (companyId: number) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation({
    mutationFn: (payload: BulkOrgChartUpdate) =>
      UserAPI.bulkUpdateOrgChart(payload),
    onMutate: async () => {
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      return {
        fallback: previous,
      };
    },
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) => {
      const { fallback } = context || {};

      if (fallback) {
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
      }
      if (err instanceof Error && err?.request?.status === 403) {
        const errorMsg = translate('settings.team_members.edit.error');

        toastService({
          message: errorMsg,
        }).error();
      }
    },
    onSuccess: (data: User[]) => {
      const previous = queryClient.getQueryData<MembersResponse>(
        ALL_MEMBERS_QUERY_KEY,
      );

      let finalMembers = {};

      if (previous !== undefined) {
        finalMembers = data.reduce(
          (oldMembers, currentUser) =>
            patchUsersByPayload(oldMembers, currentUser),
          previous,
        );
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);
      }

      if (isEmpty(finalMembers)) {
        queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      }
      queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
      queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
    },
  });
};

export const useReplaceUserOrgChart = (companyId: number) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation({
    mutationFn: ({ oldUser, newUser }: { oldUser: number; newUser: number }) =>
      UserAPI.replaceUserInOrgChart(oldUser, newUser),
    onMutate: async () => {
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      return {
        fallback: previous,
      };
    },
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) => {
      const { fallback } = context || {};

      if (fallback) {
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
      }
      if (err instanceof Error && err?.request?.status === 403) {
        const errorMsg = translate('settings.team_members.edit.error');

        toastService({
          message: errorMsg,
        }).error();
      }
    },
    onSuccess: (data, variables) => {
      const previous = queryClient.getQueryData<MembersResponse>(
        ALL_MEMBERS_QUERY_KEY,
      );
      const { oldUser, newUser } = variables;

      let finalMembers = {};

      const newUserIndex = previous?.members.findIndex(
        (member) => member.user.userId === newUser,
      );

      const oldUserIndex = previous?.members.findIndex(
        (member) => member.user.userId === oldUser,
      );

      if (newUserIndex && oldUserIndex && previous) {
        const newUserData = previous.members[newUserIndex].user;
        const oldUserData = previous.members[oldUserIndex].user;

        newUserData.reportingTo = oldUserData.reportingTo;
        newUserData.managerOf = oldUserData.managerOf;

        oldUserData.reportingTo = undefined;
        oldUserData.managerOf = undefined;

        const newUserPatchedData = patchUsersByPayload(previous, newUserData);

        finalMembers = patchUsersByPayload(newUserPatchedData, oldUserData);
      }

      queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);

      queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
      queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
    },
  });
};
